/**
 * Stylux Personalization Offer Widget (PDP)
 * ADDED: 2026-07-30 by MeshPlanet
 *
 * <mp-stylux-offer> wraps the Stylux offer container on the product page. The
 * SDK injects its offer button inside that container only when the selected
 * variant is customizable, so everything the card renders (image, copy, price)
 * must stay hidden until that button actually shows up — and go away again when
 * the shopper switches to a variant without personalization.
 *
 * On top of visibility the element takes over the injected button:
 *   - the SDK label ("PERSONALIZE IT - $40") is replaced by data-button-label
 *     and kept as the accessible name, since "ADD" alone says nothing
 *   - the amount found in that label is rendered in the card's price element,
 *     the only place the offer price is exposed to the theme
 *
 * Data attributes:
 *   data-button-label  label forced on the injected offer button
 *   data-price-prefix  string prepended to the amount (e.g. "+")
 *   data-show-price    'true' to display the offer amount in the card
 */
(function () {
  'use strict';

  /* Amount inside the SDK label: "PERSONALIZE IT - $40", "... - USD $40.00".
     The optional prefix is 3 letters so a label word ("IT $40") is not read as
     a currency code. */
  var PRICE_PATTERN = /(?:[A-Z]{3}\s*)?[$€£¥]\s*\d[\d.,]*/;

  class MpStyluxOffer extends HTMLElement {
    static get observedAttributes() {
      return ['hidden'];
    }

    /* The theme morphs the buy buttons on variant changes and can put back the
       hidden attribute rendered by Liquid while the offer button is still
       injected, which would leave the offer unreachable. Re-deriving the state
       from the container settles it (sync only writes hidden when it differs,
       so this does not loop). */
    attributeChangedCallback() {
      if (this.container) this.sync();
    }

    connectedCallback() {
      /** @type {HTMLElement | null} */
      this.container = /** @type {HTMLElement | null} */ (this.querySelector('[data-stylux-container]'));
      /** @type {HTMLElement | null} */
      this.priceElement = /** @type {HTMLElement | null} */ (this.querySelector('[data-stylux-price]'));

      if (!this.container) return;

      /* The SDK renders asynchronously and re-renders on variant changes, so
         both the button showing up and its label changing must be caught. */
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.container, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      this.sync();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    /** @returns {HTMLElement | null} */
    get offerButton() {
      if (!this.container) return null;

      return /** @type {HTMLElement | null} */ (this.container.querySelector('.stylux-offer-btn, button'));
    }

    sync() {
      var button = this.offerButton;

      /* Only written when it actually changes: assigning the same value still
         fires attributeChangedCallback, which would recurse. */
      if (!button) {
        if (!this.hidden) this.hidden = true;
        return;
      }

      this.applyLabel(button);

      if (this.hidden) this.hidden = false;
    }

    /**
     * Shortens the button label and keeps the original SDK text for the price
     * lookup and the accessible name.
     *
     * @param {HTMLElement} button
     */
    applyLabel(button) {
      var label = (this.dataset.buttonLabel || '').trim();
      var ctaText = button.dataset.mpStyluxCta;

      if (ctaText === undefined) {
        ctaText = (button.textContent || '').trim();
        /* Stored on the button: a re-injected button is a new node and carries
           the SDK label again, so the amount is never read from our own text. */
        button.dataset.mpStyluxCta = ctaText;
      }

      this.renderPrice(ctaText);

      if (!label || (button.textContent || '').trim() === label) return;

      button.setAttribute('aria-label', ctaText);
      button.textContent = label;
    }

    /** @param {string} ctaText */
    renderPrice(ctaText) {
      if (!this.priceElement) return;

      var match = this.dataset.showPrice === 'true' ? String(ctaText).match(PRICE_PATTERN) : null;

      if (!match) {
        this.priceElement.textContent = '';
        this.priceElement.hidden = true;
        return;
      }

      var prefix = (this.dataset.pricePrefix || '').trim();

      this.priceElement.textContent = prefix ? prefix + ' ' + match[0].trim() : match[0].trim();
      this.priceElement.hidden = false;
    }
  }

  if (!customElements.get('mp-stylux-offer')) {
    customElements.define('mp-stylux-offer', MpStyluxOffer);
  }
})();
